'use client';

import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import {
	ArrowForwardIcon,
	Css,
	H4,
	IResponsiveImageSource,
	Responsive,
	StyledCardContent,
	StyledCardPicture,
	IStyledCardProps,
	ITheme,
	Markdown,
} from '../../../../ui';
import { ArrowLinkStyles } from '../../button/ArrowLinkStyles';
import BaseCard from '../BaseCard';
import { renderMarkdownAsPlainText } from '../../../../utils/renderMarkdownAsPlainText';
import { Color } from '../../../../styles/Color';
import { formatFullDate } from '../../../../i18n/formatFullDate';
import { IContentCategoryEntry } from '../../../../contentful/types/IContentCategoryEntry';
import { MarkdownRenderContextProvider } from '../../markdown';

interface ICardProps extends IStyledCardProps {
	type?: 'card' | 'feature' | 'news';
	title: string | ReactNode;
	body?: string;
	imageSource?: string | IResponsiveImageSource;
	icon?: ReactNode;
	linkHref?: string;
	linkTitle?: string;
	fullyClickable?: boolean;
	imageAlt?: string;
	publicationDate?: string;
	categories?: IContentCategoryEntry[];
	renderBodyAsMarkdown?: boolean;
}

export default function Card({
	type = 'card',
	title,
	body,
	linkHref,
	linkTitle = 'Read more',
	imageSource,
	icon,
	fullyClickable = true,
	imageAlt,
	publicationDate,
	categories,
	renderBodyAsMarkdown,
	...props
}: ICardProps) {
	const plainBody = renderMarkdownAsPlainText(body);

	return (
		<BaseCard
			fullyClickable={fullyClickable}
			linkHref={linkHref}
			animationType={type === 'feature' ? 'boxShadow' : 'zoom'}
			{...props}
		>
			{imageSource && (
				<CardPictureStyled
					source={imageSource}
					alt={imageAlt || `Image for ${title}`}
				/>
			)}

			{!!categories?.length && (
				<CardCategories>
					{categories.map((category) => (
						<CardCategory key={category.id}>
							{category.fields.categoryLabel}
						</CardCategory>
					))}
				</CardCategories>
			)}

			<CardContentStyled
				fullyClickable={fullyClickable}
				isFeatureEntrypoint={type === 'feature'}
			>
				{icon && <CardIcon>{icon}</CardIcon>}

				<div>
					<small>
						{publicationDate && formatFullDate(publicationDate, 'MMM d, yyyy')}
					</small>
					{typeof title === 'string' ? (
						<CardTitle type={type}>{title}</CardTitle>
					) : (
						title
					)}
					{!renderBodyAsMarkdown && plainBody && (
						<CardBody type={type} withTruncation={fullyClickable}>
							{plainBody}
						</CardBody>
					)}

					{renderBodyAsMarkdown && body && (
						<CardBody type={type} withTruncation={fullyClickable}>
							<MarkdownRenderContextProvider>
								<Markdown source={body} />
							</MarkdownRenderContextProvider>
						</CardBody>
					)}
				</div>
				{fullyClickable && (
					<CardLink type={type}>
						<span>{linkTitle}</span> <ArrowForwardIcon aria-hidden />
					</CardLink>
				)}
			</CardContentStyled>
		</BaseCard>
	);
}

const CardPictureStyled = styled(StyledCardPicture)<{}, ITheme>`
	overflow: hidden;
	border-radius: ${(props) =>
		`${props.theme.card.borderRadius} ${props.theme.card.borderRadius} 0 0`};
	img {
		transition: 0.3s ease-in;
		aspect-ratio: 2/1;
		width: 100%;
		object-fit: cover;
		height: 190px;
	}
`;

const ICON_SIZE_DESKTOP = '32px';
const ICON_SIZE_MOBILE = '24px';
const ICON_MARGIN_BOTTOM = Css.pxToRem(24);

const CardIcon = styled.div<{}, ITheme>`
	img {
		width: ${ICON_SIZE_DESKTOP};
		height: ${ICON_SIZE_DESKTOP};
		margin-bottom: ${ICON_MARGIN_BOTTOM};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			width: ${ICON_SIZE_MOBILE};
			height: ${ICON_SIZE_MOBILE};
		}
	}

	svg {
		width: ${ICON_SIZE_DESKTOP};
		height: ${ICON_SIZE_DESKTOP};
		margin-bottom: ${ICON_MARGIN_BOTTOM};

		path {
			fill: ${(props) => props.theme.card.icon.color};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			width: ${ICON_SIZE_MOBILE};
			height: ${ICON_SIZE_MOBILE};
		}
	}
`;

const CardContentStyled = styled(StyledCardContent)<
	{
		fullyClickable: boolean;
		isFeatureEntrypoint: boolean;
	},
	ITheme
>`
	flex: 1;
	justify-content: ${(props) =>
		props.isFeatureEntrypoint ? 'unset' : 'space-between'};

	padding: ${(props) =>
		props.isFeatureEntrypoint
			? `${Css.pxToRem(16)}`
			: props.fullyClickable
			? `${Css.pxToRem(14)} ${Css.pxToRem(16)} ${Css.pxToRem(20)}`
			: `${Css.pxToRem(24)} ${Css.pxToRem(16)}`};
	border-radius: ${(props) => props.theme.card.borderRadius};

	h5 {
		margin-top: ${Css.pxToRem(12)};
		line-height: 30px;
	}

	${({ isFeatureEntrypoint }) =>
		isFeatureEntrypoint &&
		css`
			background-color: ${Color.Neutral2};
		`}
`;

export const CardTitle = styled(H4)<{ type: ICardProps['type'] }, ITheme>`
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-height: normal;

	${({ type }) =>
		type === 'news' &&
		`font-size: ${Css.pxToRem(24)}; margin-top: ${Css.pxToRem(12)};`}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		${({ type }) =>
			type === 'feature' &&
			css`
				font-size: ${Css.pxToRem(24)};
			`}
		margin-bottom: 0.1em;
	}
`;

export const CardBody = styled.div<{
	type?: ICardProps['type'];
	withTruncation: boolean;
}>`
	font-size: ${Css.pxToRem(16)};
	font-weight: 300;
	line-height: ${Css.pxToRem(24)};
	color: ${Color.OnBackground2};
	overflow: hidden;

	display: ${(props) => (props.withTruncation ? '-webkit-box' : 'block')};
	-webkit-line-clamp: ${({ type }) => (type === 'news' ? 3 : 2)};
	-webkit-box-orient: vertical;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		-webkit-line-clamp: 2;
	}
`;

const CardLink = styled.div<{ type: ICardProps['type'] }, ITheme>`
	${(props) => ArrowLinkStyles(props.theme)}
	padding: 0;
	margin-top: ${({ type }) => {
		switch (type) {
			case 'feature':
				return `${Css.pxToRem(24)}`;
			case 'news':
				return `${Css.pxToRem(30)}`;
			default:
				return `${Css.pxToRem(55)}`;
		}
	}};
	align-self: flex-end;
	align-items: center;
`;

const CardCategories = styled.div`
	position: absolute;
	display: flex;
	flex-wrap: wrap;
	gap: ${Css.pxToRem(4)};
	justify-content: flex-end;
	top: ${Css.pxToRem(8)};
	right: ${Css.pxToRem(8)};
	left: ${Css.pxToRem(8)};
`;

const CardCategory = styled.div`
	background-color: ${Color.CategoryBackground};
	padding: ${Css.pxToRem(8)} ${Css.pxToRem(12)};
	border-radius: 7px;
	color: ${Color.CategoryText};
`;
